import React, { useState, useEffect } from "react";
import axios from 'axios';
import './NewLeadForm.css';

const NewLeadForm = ({ mode = 'create', leadId = null, onLeadCreated, onCancel, currentUserFullName }) => {
    const userType = localStorage.getItem("usertype");
    const loggedInUserId = localStorage.getItem("id") || localStorage.getItem("user_id");

    const [formData, setFormData] = useState({
        lead_title: '',
        lead_name: '',
        lead_email: '',
        lead_phone: '',
        lead_address: '',
        city: '',
        state: '',
        country: '',
        lead_source: '',
        lead_link: '',
        fb_url: '',
        twitter_url: '',
        skype_id: '',
        short_note: '',
        lead_status: 'New',
        reporter: currentUserFullName || 'Administrator',
        assigned_to_type: 'customize',
        group_id: 0,
        mode: mode
    });

    const [groupsData, setGroupsData] = useState({ administrators: [], groups: [] });
    const [selectedGroupId, setSelectedGroupId] = useState('');
    const [permissions, setPermissions] = useState({});
    const [loading, setLoading] = useState(false);
    const [loadingLead, setLoadingLead] = useState(false);
    const [saving, setSaving] = useState(false);

    // ==================== FIX: Load Groups when customizing ====================
    useEffect(() => {
        if (formData.assigned_to_type === 'customize' && groupsData.groups.length === 0) {
            fetchGroups();
        }
    }, [formData.assigned_to_type]);

    // ==================== FIX: Load existing lead when Editing ====================
    useEffect(() => {
        if (mode === 'edit' && leadId) {
            loadLeadForEdit(leadId);
        }
    }, [mode, leadId]);

    const fetchGroups = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem("token");
            const res = await axios.get('https://skcapi.btcjapan.co/get_groups_with_members.php', {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (res.data.success) {
                setGroupsData(res.data);
            }
        } catch (err) { console.error(err); } finally { setLoading(false); }
    };

    // ==================== FIX: The Correct API to fetch the specific Lead ====================
    const loadLeadForEdit = async (id) => {
        setLoadingLead(true);
        try {
            const token = localStorage.getItem("token");
            const res = await axios.get(`https://skcapi.btcjapan.co/get_single_lead.php?id=${id}`, { 
                headers: { Authorization: `Bearer ${token}` } 
            });

            if (res.data.success) {
                const data = res.data.data;
                setFormData({
                    lead_title: data.lead_title || '',
                    lead_name: data.lead_name || '',
                    lead_email: data.lead_email || '',
                    lead_phone: data.lead_phone || '',
                    lead_address: data.lead_address || '',
                    city: data.city || '',
                    state: data.state || '',
                    country: data.country || '',
                    lead_source: data.lead_source || '',
                    lead_link: data.lead_link || '',
                    fb_url: data.fb_url || '',
                    twitter_url: data.twitter_url || '',
                    skype_id: data.skype_id || '',
                    short_note: data.short_note || '',
                    lead_status: data.lead_status || 'New',
                    reporter: data.reporter || currentUserFullName,
                    assigned_to_type: data.assigned_to_type || 'customize',
                    group_id: data.group_id || 0,
                    mode: 'edit'
                });
                
                // Select the group
                if(data.group_id) setSelectedGroupId(String(data.group_id));

                // Fetch groups for the dropdown
                const groupsRes = await axios.get('https://skcapi.btcjapan.co/get_groups_with_members.php', {
                    headers: { Authorization: `Bearer ${token}` }
                });
                if (groupsRes.data.success) {
                    setGroupsData(groupsRes.data);
                }

                // ===== Load the selected members from the database =====
                if (data.assigned_to_type === 'customize') {
                    const permObj = {};
                    (data.permissions || []).forEach(p => {
                        permObj[p.user_id] = {
                            can_view: Number(p.can_view) === 1,
                            can_edit: Number(p.can_edit) === 1,
                            can_delete: Number(p.can_delete) === 1,
                            is_auto: false
                        };
                    });
                    setPermissions(permObj);
                }
            }
        } catch (err) { 
            console.error(err); 
            alert("Error loading lead data");
        } finally { 
            setLoadingLead(false); 
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (name === 'assigned_to_type' && value === 'customize') {
            setSelectedGroupId('');
            setPermissions({});
        }
    };

    const handleGroupChange = (e) => {
        const groupId = e.target.value;
        setSelectedGroupId(groupId);
        if (!groupId) { setPermissions({}); return; }

        const group = groupsData.groups.find(g => g.group_id == groupId);
        if (!group) return;

        const newPermissions = {};
        groupsData.administrators.forEach(admin => {
            newPermissions[admin.id] = { can_view: true, can_edit: true, can_delete: true, is_auto: true };
        });
        if (group.leader_id) {
            newPermissions[group.leader_id] = { can_view: true, can_edit: true, can_delete: true, is_auto: true };
        }

        setPermissions(prev => {
            const merged = { ...newPermissions };
            Object.keys(prev).forEach(uid => {
                if (!merged[uid] && !prev[uid].is_auto) { merged[uid] = prev[uid]; }
            });
            return merged;
        });
    };

    const toggleMember = (userId, checked) => {
        if (checked) {
            setPermissions(prev => ({
                ...prev,
                [userId]: { can_view: true, can_edit: false, can_delete: false, is_auto: false }
            }));
        } else {
            setPermissions(prev => {
                const copy = { ...prev };
                if (copy[userId]?.is_auto) return prev;
                delete copy[userId];
                return copy;
            });
        }
    };

    const handlePermissionChange = (userId, permType, checked) => {
        setPermissions(prev => {
            const current = prev[userId] || { can_view: false, can_edit: false, can_delete: false };
            return {
                ...prev,
                [userId]: { ...current, [permType]: checked }
            };
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.lead_name || !formData.lead_email || !formData.lead_phone) {
            alert("Lead Name, Email, and Phone are required.");
            return;
        }
        setSaving(true);
        try {
            const token = localStorage.getItem("token");
            let permissionsPayload = [];
            if (formData.assigned_to_type === 'customize') {
                if (!selectedGroupId) { alert("Please select a group"); setSaving(false); return; }
                permissionsPayload = Object.entries(permissions).map(([userId, perms]) => ({
                    user_id: parseInt(userId),
                    can_view: perms.can_view ? 1 : 0,
                    can_edit: perms.can_edit ? 1 : 0,
                    can_delete: perms.can_delete ? 1 : 0
                }));
            }

            const payload = { 
                ...formData, 
                group_id: selectedGroupId ? parseInt(selectedGroupId, 10) : 0,
                permissions: permissionsPayload
            };
            
            if (mode === 'edit') payload.id = leadId;

            await axios.post('https://skcapi.btcjapan.co/create_update_lead.php', payload, {
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
            });

            alert(mode === 'edit' ? 'Lead updated successfully!' : 'Lead created successfully!');
            if (onLeadCreated) onLeadCreated();
        } catch (err) {
            console.error(err);
            if (err.response && err.response.status === 409) alert(err.response.data.error);
            else alert("Error saving lead.");
        } finally { setSaving(false); }
    };

    // ==================== DROPDOWN FILTER LOGIC ====================
    let displayGroups = [];
    if (userType === 'administrator') {
        displayGroups = groupsData.groups;
    } else if (userType === 'leader') {
        displayGroups = groupsData.groups.filter(g => String(g.leader_id) === String(loggedInUserId));
    } else if (userType === 'member') {
        displayGroups = groupsData.groups.filter(group => 
            group.members.some(m => String(m.id) === String(loggedInUserId))
        );
    }

    const selectedGroup = displayGroups.find(g => g.group_id == selectedGroupId);
    const selectedGroupName = displayGroups.find(g => g.group_id == selectedGroupId);

    useEffect(() => {
        if (selectedGroupName && selectedGroupName.leader_name) {
            setFormData(prev => ({
                ...prev,
                reporter: selectedGroupName.leader_name
            }));
        }
    }, [selectedGroupName]);

    // ==================== AUTO-SELECT MEMBER ====================
    useEffect(() => {
        if (userType === 'member' && selectedGroup) {
            const isMemberInGroup = selectedGroup.members.some(m => String(m.id) === String(loggedInUserId));
            if (isMemberInGroup && !permissions[loggedInUserId]) {
                setPermissions(prev => ({
                    ...prev,
                    [loggedInUserId]: { can_view: true, can_edit: false, can_delete: false, is_auto: true }
                }));
            }
        }
    }, [selectedGroup, loggedInUserId, userType]);

    if (loadingLead) return <div style={{ padding: 40, textAlign: 'center', color: '#666' }}>Loading lead data...</div>;

        return (
        <div className={`new-leads-form-container ${mode === 'edit' ? 'sale-order-drawer-form' : ''}`}>
            <form onSubmit={handleSubmit} className="sale-order-form">
                
                {/* ===== HERO SECTION ===== */}
                <div className="lead-form-hero">
                    <div>
                        <div className="lead-form-eyebrow">
                            <span className="eyebrow-dot"></span>
                            NEW LEAD
                        </div>
                        <h2>{mode === 'edit' ? 'Edit Lead' : 'Create New Lead'}</h2>
                        <p>Fill in the customer details below to generate this document.</p>
                    </div>
                    <div className="lead-form-hero-actions">
                        <button type="submit" className="btn-blue hero-save-btn">{mode === 'edit' ? 'Update Lead' : 'Save Lead'}</button>
                    </div>
                </div>

                {/* ===== CUSTOMER INFORMATION ===== */}
                <div className="form-section-card-lead">
                    <div className="section-card-heading compact">
                        <div className="section-title-wrap">
                            <div className="section-icon">👤</div>
                            <div>
                                <h3>Customer Information</h3>
                                <p>Provide the basic customer details.</p>
                            </div>
                        </div>
                    </div>
                    
                    <div className="form-fields-grid form-fields-grid-3">
                        <div className="form-group">
                            <label>Lead Title <span>*</span></label>
                            <input type="text" name="lead_title" value={formData.lead_title} onChange={handleChange} />
                        </div>
                        <div className="form-group">
                            <label>Lead Status</label>
                            <select name="lead_status" value={formData.lead_status} onChange={handleChange}>
                                <option>New</option><option>Contacted</option><option>Qualified</option><option>Lost</option>
                            </select>
                        </div>
                        <div className="form-group">
                            <label>Lead Source</label>
                            <select name="lead_source" value={formData.lead_source} onChange={handleChange}>
                                <option value="">Select Source</option>
                                <option>Facebook</option><option>Instagram</option><option>Google</option>
                                <option>WhatsApp</option><option>X (Twitter)</option><option>Call</option>
                                <option>Email</option><option>Website</option><option>Referral</option>
                            </select>
                        </div>

                        <div className="form-group">
                            <label>Lead Link</label>
                            <input type="text" name="lead_link" value={formData.lead_link} onChange={handleChange} placeholder="https://..." />
                        </div>
                        <div className="form-group">
                            <label>Lead Name <span>*</span></label>
                            <input type="text" name="lead_name" value={formData.lead_name} onChange={handleChange} />
                        </div>
                        <div className="form-group">
                            <label>Lead Email <span>*</span></label>
                            <input type="email" name="lead_email" value={formData.lead_email} onChange={handleChange} />
                        </div>

                        <div className="form-group">
                            <label>Lead Phone <span>*</span></label>
                            <input type="text" name="lead_phone" value={formData.lead_phone} onChange={handleChange} />
                        </div>
                        <div className="form-group">
                            <label>Lead Address</label>
                            <textarea rows="1" name="lead_address" value={formData.lead_address} onChange={handleChange}></textarea>
                        </div>
                        <div className="form-group">
                            <label>City</label>
                            <input type="text" name="city" value={formData.city} onChange={handleChange} />
                        </div>

                        <div className="form-group">
                            <label>State</label>
                            <input type="text" name="state" value={formData.state} onChange={handleChange} />
                        </div>
                        <div className="form-group">
                            <label>Country</label>
                            <input type="text" name="country" value={formData.country} onChange={handleChange} />
                        </div>
                        <div className="form-group">
                            <label>Reporter</label>
                            <input type="text" value={formData.reporter} readOnly={true} />
                        </div>

                        <div className="form-group">
                            <label>Facebook URL</label>
                            <input type="text" name="fb_url" value={formData.fb_url} onChange={handleChange} />
                        </div>
                        <div className="form-group">
                            <label>Twitter URL</label>
                            <input type="text" name="twitter_url" value={formData.twitter_url} onChange={handleChange} />
                        </div>
                        <div className="form-group">
                            <label>Skype ID</label>
                            <input type="text" name="skype_id" value={formData.skype_id} onChange={handleChange} />
                        </div>
                    </div>
                </div>

                {/* ===== PERMISSION ACCESS ===== */}
                <div className="form-section-card-lead permission-section-card">
                    <div className="section-card-heading">
                        <div className="section-title-wrap">
                            <div className="section-icon">🔐</div>
                            <div>
                                <h3>Permission Access</h3>
                                <p>Assign this lead to specific users or groups.</p>
                            </div>
                        </div>
                        <div className="assigned-radio-group">
                            {/* 
                            <label className="assigned-radio-option permission-choice">
                                <input type="radio" name="assigned_to_type" value="everyone" checked={formData.assigned_to_type === 'everyone'} onChange={handleChange} />
                                <span><strong>Everyone</strong><small>All users can view</small></span>
                            </label>
                            */}
                            {(userType === 'administrator' || userType === 'leader') && (
                                <label className="assigned-radio-option permission-choice">
                                    <input type="radio" name="assigned_to_type" value="customize" checked={formData.assigned_to_type === 'customize'} onChange={handleChange} />
                                    <span><strong>Customize Permission</strong><small>Assign to group</small></span>
                                </label>
                            )}
                        </div>
                    </div>

                    {formData.assigned_to_type === 'customize' && (
                        <div className="permission-panel">
                            {loading ? <p className="permission-panel-loading">Loading groups...</p> : (
                                <>
                                    <div className="group-select-block">
                                        <label>Select Group <span>*</span></label>
                                        <select value={selectedGroupId} onChange={handleGroupChange}>
                                            <option value="">-- Select a Group --</option>
                                            {displayGroups.map(g => <option key={g.group_id} value={g.group_id}>{g.group_name}</option>)}
                                        </select>
                                        {displayGroups.length === 0 && <p className="group-empty-msg">No groups available for your account.</p>}
                                    </div>

                                    {selectedGroup && (
                                        <div className="permission-scroll">
                                            <div className="role-block">
                                                <p className="role-block-title admin">Administrators <span>(Auto)</span></p>
                                                {groupsData.administrators.map(admin => (
                                                    <div key={admin.id} className="role-card admin">
                                                        <strong>{admin.fname} {admin.lname}</strong>
                                                        <span className="role-card-tag">(Administrator)</span>
                                                        <div className="role-card-perms">✓ Can View &nbsp; ✓ Can Edit &nbsp; ✓ Can Delete</div>
                                                    </div>
                                                ))}
                                            </div>

                                            <div className="role-block">
                                                <p className="role-block-title leader">Group Leader <span>(Auto)</span></p>
                                                <div className="role-card leader">
                                                    <strong>{selectedGroup.leader_name}</strong>
                                                    <span className="role-card-tag">(Leader)</span>
                                                    <div className="role-card-perms">✓ Can View &nbsp; ✓ Can Edit &nbsp; ✓ Can Delete</div>
                                                </div>
                                            </div>

                                            <div className="role-block">
                                                <p className="role-block-title member">Group Members</p>
                                                {selectedGroup.members.filter(m => m.id != selectedGroup.leader_id).map(member => {
                                                    const isSelected = !!permissions[member.id];
                                                    const isSelf = String(member.id) === String(loggedInUserId) && userType === 'member';
                                                    const isDisabled = userType === 'member' || isSelf;
                                                    return (
                                                        <div key={member.id} className={`member-card ${isSelected ? 'selected' : ''}`}>
                                                            <label className="member-card-label">
                                                                <input type="checkbox" checked={isSelected || isSelf} disabled={isDisabled} onChange={(e) => toggleMember(member.id, e.target.checked)} />
                                                                {member.fname} {member.lname} {isSelf && <span className="member-self-tag">(You)</span>}
                                                                <span className="member-role-tag">({member.usertype})</span>
                                                            </label>
                                                            {isSelected && (
                                                                <div className="member-permissions-row">
                                                                    <label><input type="checkbox" checked={!!permissions[member.id]?.can_view} onChange={(e) => handlePermissionChange(member.id, 'can_view', e.target.checked)} /> Can View</label>
                                                                    <label><input type="checkbox" checked={!!permissions[member.id]?.can_edit} onChange={(e) => handlePermissionChange(member.id, 'can_edit', e.target.checked)} /> Can Edit</label>
                                                                    <label><input type="checkbox" checked={!!permissions[member.id]?.can_delete} onChange={(e) => handlePermissionChange(member.id, 'can_delete', e.target.checked)} /> Can Delete</label>
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    )}
                </div>

                {/* ===== SHORT NOTE ===== */}
                <div className="form-section-card-lead">
                    <div className="section-card-heading compact">
                        <div className="section-title-wrap">
                            <div className="section-icon">📝</div>
                            <div>
                                <h3>Short Note</h3>
                            </div>
                        </div>
                    </div>
                    <div className="description-editor">
                        <div className="description-toolbar">
                            <button type="button">B</button><button type="button">I</button><button type="button">U</button>
                        </div>
                        <textarea rows="3" name="short_note" value={formData.short_note} onChange={handleChange} />
                    </div>
                </div>

                <div className="form-submit-row">
                    {mode === 'edit' && <button type="button" className="btn-secondary" onClick={onCancel}>Cancel</button>}
                    <button type="submit" className="btn-blue final-save-btn" disabled={saving}>{saving ? 'Saving...' : (mode === 'edit' ? 'Update Lead' : 'Save Lead')}</button>
                </div>
            </form>
        </div>
    );
};

export default NewLeadForm;